import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Route, Routes, useLocation } from "react-router-dom";
import { TopNav } from "./components/TopNav";
import { SiteFooter } from "./components/SiteFooter";
import { Testimonials } from "./components/Testimonials";
import Landing from "./pages/Landing";
import Docs from "./pages/Docs";
import DocArticle from "./pages/DocArticle";
import RegisterSeller from "./pages/RegisterSeller";
import NotFound from "./pages/NotFound";

/**
 * Marketing site shell.
 *   /                  → landing page (install-driven, single CTA)
 *   /docs              → docs index (curated article list)
 *   /docs/:slug        → single article
 *   /register          → seller registration form
 *   *                  → 404
 */
export default function App() {
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Sync <title> and <meta description> with the active locale.
  useEffect(() => {
    document.title = t("meta.title");
    const descriptionEl = document.querySelector('meta[name="description"]');
    if (descriptionEl) {
      descriptionEl.setAttribute("content", t("meta.description"));
    }
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute("content", t("meta.title"));
    }
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute("content", t("meta.description"));
    }
    document.documentElement.lang = i18n.language;
  }, [i18n.language, t, location.pathname]);

  useEffect(() => {
    if (location.hash) {
      // wait one frame so the target element is in the DOM
      requestAnimationFrame(() => {
        const el = document.getElementById(location.hash.slice(1));
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [location.pathname, location.hash]);

  return (
    <div className="flex min-h-full w-full max-w-full flex-col overflow-x-clip">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-50 focus:rounded-md focus:bg-[var(--color-ink)] focus:px-3 focus:py-1.5 focus:text-[12px] focus:text-white"
      >
        {t("meta.skipToContent")}
      </a>
      <TopNav />
      <main id="main" className="w-full max-w-full flex-1 overflow-x-clip">
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/docs" element={<Docs />} />
          <Route path="/docs/:slug" element={<DocArticle />} />
          <Route path="/register" element={<RegisterSeller />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {location.pathname === "/" ? <Testimonials /> : null}
      <SiteFooter />
    </div>
  );
}
